import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { Segment } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { findSelectedMenu, redirectTo } from '../../helpers/menuTree/menuTree';
import { setFormsLanguage } from '../../redux/modules/global';
import { getUser } from '../../redux/modules/user';
import Loader from '../Loaders/Loader';
import MainButton from '../MUI/MainButton';
import headerImage from '../../img/efesHomepageHeaderImage.png';
import { TRAVEL_CALCULATOR_PATH, TRAVEL_CONTRACT_SIGN_PATH } from '../../constants/routes';
import useIsHomePage from '../../hooks/useIsHomePage';
import HeaderMenu from './HeaderMenu';
import BurgerMenu from '../../icons/BurgerMenu';
import EfesLogoLightWithText from '../../img/efesLogoLightWithText.png';

import styles from './EfesHeader.module.scss';

const EfesHeader = ({ menuClickHandler, routeTree, handleNavigation }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const { pathname } = useLocation();
  const isHomePage = useIsHomePage();

  const isLoginBlockOpen = useSelector((state) => state.loginBlock.isOpen);
  const isAuth = useSelector((state) => state.auth.isAuthenticated);
  const loadingUser = useSelector((state) => state.userReducer.loadingUser);

  const [activeItem, setActiveItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  useEffect(() => {
    const selectedMenuItem = findSelectedMenu(routeTree, pathname);
    setSelectedItem(selectedMenuItem);
    setActiveItem(selectedMenuItem);
  }, [pathname, routeTree]);

  useEffect(() => {
    if (isAuth) {
      dispatch(getUser());
    }
  }, [dispatch, isAuth]);

  const navbar = document.getElementById('header');
  useEffect(() => {
    // Function to toggle the sticky class based on scroll position
    const toggleStickyNavbar = () => {
      if (navbar) {
        const stickyOffset = navbar.offsetTop;
        setIsHeaderSticky(window.scrollY > stickyOffset);
      }
    };

    window.addEventListener('scroll', toggleStickyNavbar);

    return () => {
      window.removeEventListener('scroll', toggleStickyNavbar);
    };
  }, [navbar]);

  const handleChangeLanguage = useCallback(
    (language) => {
      i18n.changeLanguage(language);
      dispatch(setFormsLanguage(language));
    },
    [i18n, dispatch],
  );

  if (loadingUser) {
    return <Loader />;
  }

  return (
    <Segment
      className={cn(styles.efesDesktopHeader, { [styles.stickyHeader]: !isHomePage })}
      textAlign="center"
      vertical
    >
      {isHomePage ? (
        <>
          {isHeaderSticky && (
            <div className={styles.stickyHomepageHeader}>
              <HeaderMenu
                activeItem={activeItem}
                isHomePage={isHomePage}
                isHeaderSticky={isHeaderSticky}
                handleChangeLanguage={handleChangeLanguage}
                routeTree={routeTree}
                setActiveItem={setActiveItem}
                selectedItem={selectedItem}
                handleNavigation={handleNavigation}
                logo={EfesLogoLightWithText}
                mobileLogo={EfesLogoLightWithText}
                styles={styles}
              />
            </div>
          )}
          <div className={styles.headerMenuViewPort}>
            <div className={cn(styles.headerMenuContainer)}>
              <HeaderMenu
                activeItem={activeItem}
                isHomePage={isHomePage}
                isHeaderSticky={isHeaderSticky}
                handleChangeLanguage={handleChangeLanguage}
                routeTree={routeTree}
                setActiveItem={setActiveItem}
                selectedItem={selectedItem}
                handleNavigation={handleNavigation}
                logo={EfesLogoLightWithText}
                mobileLogo={EfesLogoLightWithText}
                styles={styles}
              />
              <div className={styles.headerContent}>
                <Typography className={styles.headerContentTitle}>
                  {t('home.headerTitle')}
                </Typography>
                <Typography className={styles.headerContentDescription}>
                  {t('home.headerDescription')}
                </Typography>
                <div className={styles.headerContentButtons}>
                  <MainButton
                    variant="contained"
                    color="primary"
                    className={cn({
                      [styles.buyButton]: true,
                      [styles.calculatorButton]: true,
                    })}
                    onClick={(e) => {
                      redirectTo(history, TRAVEL_CALCULATOR_PATH, e);
                    }}
                  >
                    {t('home.calculator')}
                  </MainButton>

                  <MainButton
                    variant="contained"
                    color="primary"
                    className={styles.buyButton}
                    onClick={(e) => {
                      redirectTo(history, `${TRAVEL_CONTRACT_SIGN_PATH}/signer`, e);
                    }}
                  >
                    {t('home.buy')}
                  </MainButton>
                </div>
              </div>
              <div className={styles.imageContainer}>
                <img src={headerImage} className={styles.headerImage} alt="" />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.simpleHeaderMenuViewPort}>
          <HeaderMenu
            activeItem={activeItem}
            isHomePage={isHomePage}
            isHeaderSticky={isHeaderSticky}
            handleChangeLanguage={handleChangeLanguage}
            routeTree={routeTree}
            setActiveItem={setActiveItem}
            selectedItem={selectedItem}
            handleNavigation={handleNavigation}
            logo={EfesLogoLightWithText}
            mobileLogo={EfesLogoLightWithText}
            styles={styles}
          />
        </div>
      )}
      <div
        className={cn({
          [styles.headerMenuLoginWrapper]: true,
          [styles.stickyTouchpadHeader]: isLoginBlockOpen,
          [styles.open]: isLoginBlockOpen,
          [styles.closed]: !isLoginBlockOpen,
        })}
      />
      <div
        role="presentation"
        className={cn(styles.burgerMenuItem, styles.mobileContent, {
          [styles.homePageStickyBurgerMenuItem]: isHomePage && isHeaderSticky,
        })}
        onClick={menuClickHandler}
      >
        <BurgerMenu />
      </div>
    </Segment>
  );
};

EfesHeader.propTypes = {
  menuClickHandler: PropTypes.func,
  handleNavigation: PropTypes.func,
  routeTree: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      icon: PropTypes.element,
      links: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          path: PropTypes.string,
        }),
      ),
    }),
  ),
};

EfesHeader.defaultProps = {
  menuClickHandler: () => { },
  handleNavigation: () => { },
  routeTree: [],
};

export default EfesHeader;
